import { PlusOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Segmented,
  Space,
  Typography,
  Table,
  Tag,
  type TableColumnsType,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Route, Routes, useNavigate } from 'react-router';

import {
  useClientsCreateClient,
  useClientsListClients,
} from '@/api/generated/endpoints/clients/clients';
import type { ClientCreate, ClientOut, ClientsListClientsStatus } from '@/api/generated/model';
import { useHasRole } from '@/auth/useHasRole';
import { ClientPage } from '@/pages/catalog/ClientPage';
import { statusOptions } from '@/pages/catalog/statusOptions';
import { CLIENT_FIELD_BY_CODE, innRules, kppRules } from '@/pages/catalog/clientForm';
import { problemMessage } from '@/i18n/problem';
import { applyProblem } from '@/lib/problemForm';
import { blankToNull } from '@/lib/text';
import { invalidatePath } from '@/lib/queryCache';
import { useIsMobile } from '@/lib/useIsMobile';
import { useTableState } from '@/lib/useTableState';

/** Раздел «Клиенты»: список и карточка клиента (/catalog/clients/:id). */
export function ClientsSection() {
  return (
    <Routes>
      <Route index element={<ClientsPage />} />
      <Route path=":clientId" element={<ClientPage />} />
    </Routes>
  );
}

function ClientsPage() {
  const { t } = useTranslation();
  const table = useTableState<'q' | 'status'>({ filters: ['q', 'status'], defaultSort: 'name' });
  const canCreate = useHasRole('storekeeper');
  const navigate = useNavigate();
  const mobile = useIsMobile();
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState(table.filters.q[0] ?? '');
  const status = (table.filters.status[0] ?? 'active') as ClientsListClientsStatus;
  const list = useClientsListClients({ ...table.query, q: table.filters.q[0] ?? null, status });

  // Телефон (T-177): клиент — одной ячейкой: название, бренд, ИНН и КПП.
  const mobileColumns: TableColumnsType<ClientOut> = [
    {
      key: 'name',
      title: t('clients.colName'),
      dataIndex: 'name',
      sorter: true,
      sortOrder: table.sortOrderFor('name'),
      render: (name: string, c) => (
        <Space direction="vertical" size={2}>
          <Space size={4} wrap>
            <a>{name}</a>
            {!c.is_active && <Tag>{t('common.archived')}</Tag>}
          </Space>
          {c.brand && <Typography.Text>{c.brand}</Typography.Text>}
          <Typography.Text type="secondary">
            {t('clients.inn')} {c.inn}
            {c.kpp ? ` · ${t('clients.kpp')} ${c.kpp}` : ''}
          </Typography.Text>
        </Space>
      ),
    },
  ];

  const columns: TableColumnsType<ClientOut> = [
    {
      key: 'name',
      title: t('clients.colName'),
      dataIndex: 'name',
      sorter: true,
      sortOrder: table.sortOrderFor('name'),
      render: (name: string, c) => (
        <Space>
          <a>{name}</a>
          {!c.is_active && <Tag>{t('common.archived')}</Tag>}
        </Space>
      ),
    },
    {
      key: 'brand',
      title: t('clients.colBrand'),
      dataIndex: 'brand',
      width: 180,
      sorter: true,
      sortOrder: table.sortOrderFor('brand'),
      render: (b: string | null) => b ?? '—',
    },
    { key: 'inn', title: t('clients.inn'), dataIndex: 'inn', width: 140 },
    {
      key: 'kpp',
      title: t('clients.kpp'),
      dataIndex: 'kpp',
      width: 120,
      render: (k: string | null) => k ?? '—',
    },
  ];

  return (
    <Card
      title={t('clients.title')}
      extra={
        canCreate && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setCreating(true);
            }}
          >
            {t('clients.add')}
          </Button>
        )
      }
    >
      <Space wrap style={{ marginBottom: 12 }}>
        <Input.Search
          allowClear
          placeholder={t('clients.searchPlaceholder')}
          style={{ width: 300 }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            if (e.target.value === '') table.setFilter('q', null);
          }}
          onSearch={(q) => {
            table.setFilter('q', q.trim() || null);
          }}
        />
        <Segmented
          options={statusOptions(t)}
          value={status}
          onChange={(v) => {
            table.setFilter('status', v === 'active' ? null : v);
          }}
        />
      </Space>
      <Table<ClientOut>
        rowKey="id"
        columns={mobile ? mobileColumns : columns}
        dataSource={list.data?.items}
        loading={list.isFetching}
        pagination={table.pagination(list.data?.total)}
        onChange={table.onTableChange}
        onRow={(c) => ({
          onClick: () => {
            void navigate(String(c.id));
          },
          style: { cursor: 'pointer' },
        })}
      />
      <ClientCreateModal
        open={creating}
        onClose={() => {
          setCreating(false);
        }}
        onCreated={(c) => {
          void navigate(String(c.id));
        }}
      />
    </Card>
  );
}

function ClientCreateModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (c: ClientOut) => void;
}) {
  const { t } = useTranslation();
  const [form] = Form.useForm<ClientCreate>();
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const create = useClientsCreateClient({
    mutation: {
      onSuccess: async (c) => {
        await invalidatePath(queryClient, '/api/v1/clients');
        void message.success(t('clients.created', { name: c.name }));
        onCreated(c);
      },
      onError: (e) => {
        if (!applyProblem(form, e, CLIENT_FIELD_BY_CODE)) setError(problemMessage(e));
      },
    },
  });

  return (
    <Modal
      title={t('clients.new')}
      open={open}
      onCancel={() => {
        setError(null);
        onClose();
      }}
      okText={t('common.create')}
      okButtonProps={{ loading: create.isPending }}
      onOk={() => {
        form.submit();
      }}
      destroyOnHidden
    >
      <Form<ClientCreate>
        form={form}
        layout="vertical"
        onFinish={(v) => {
          setError(null);
          create.mutate({
            data: { ...v, kpp: blankToNull(v.kpp), brand: blankToNull(v.brand) },
          });
        }}
      >
        <Form.Item
          name="name"
          label={t('clients.name')}
          rules={[{ required: true, whitespace: true, message: t('clients.nameRequired') }]}
        >
          <Input autoFocus maxLength={300} placeholder='АО "ВКУСВИЛЛ"' />
        </Form.Item>
        <Form.Item name="brand" label={t('clients.brand')} extra={t('clients.brandHint')}>
          <Input maxLength={100} placeholder="ВкусВилл" />
        </Form.Item>
        <Space>
          <Form.Item name="inn" label={t('clients.inn')} rules={innRules(t)}>
            <Input maxLength={12} inputMode="numeric" />
          </Form.Item>
          <Form.Item
            name="kpp"
            label={t('clients.kppHead')}
            rules={kppRules(t)}
            extra={t('clients.kppHeadHint')}
          >
            <Input maxLength={9} />
          </Form.Item>
        </Space>
      </Form>
      {error && <Alert type="error" showIcon message={error} />}
    </Modal>
  );
}
